import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closePopup } from '../../../../../components/popupPanel/popupActions';
import { PopupIds } from '../../../../../components/popupPanel/popupConstants';
import popupOpenSelector from '../../../../../components/popupPanel/popupOpenSelector';
import { getCurrentlyEditingId } from '../../../../../element/selection/currentlyEditingSelector';
import ToolbarPopup from '../../toolbarPopup/ToolbarPopup';
import RichTextTool from './RichTextTool';
import HyperlinkPopup from './hyperlinkPopup/HyperlinkPopup';
import {
    ActiveTiptapEditorSelector,
    useActiveTiptapEditorCallback,
    useTiptapActiveEditorSelector,
} from '../../../../../components/tiptapEditor/store/tiptapEditorStoreHooks';
import { useGroupTranslator } from '../../../../../components/i18n/GroupTranslationContext';
import { TooltipPositions } from '../../../../../components/tooltips/tooltipConstants';
import { getPlatformDetailsSelector } from '../../../../../platform/platformSelector';
import { isPlatformPhoneOrMobileMode } from '../../../../../platform/utils/platformDetailsUtils';
import { ToolbarItemConfig } from '../../../toolbarTypes';
import PopupTrigger from '../../../../../components/popupPanel/PopupTrigger';

const getCurrentUrl: ActiveTiptapEditorSelector = (editor) => editor?.getAttributes('link').href || '';
const getIsSelectionEmpty: ActiveTiptapEditorSelector = (editor) => editor?.state.selection.empty;

/**
 * Button for the hyperlink tool
 */
const TiptapHyperlinkToolButton: React.FC<{
    isPopupOpen: boolean;
    disabled: boolean;
    isActive: boolean;
    tooltipText?: string;
    shortcut?: string[];
}> = ({ isPopupOpen, disabled, isActive, tooltipText, shortcut }) => (
    <div style={{ cursor: disabled ? 'default' : 'pointer' }}>
        <RichTextTool
            iconName="toolbar-hyperlink"
            tooltipEnabled={!isPopupOpen}
            isOpen={isPopupOpen}
            styleCommand="link"
            disabled={disabled}
            isActive={isActive}
            tooltipText={tooltipText}
            shortcut={shortcut}
        />
    </div>
);

const TiptapHyperlinkTool = ({ item }: { item: ToolbarItemConfig }) => {
    const isPopupOpen = useSelector(popupOpenSelector(PopupIds.HYPERLINK));
    const currentlyEditingId = useSelector(getCurrentlyEditingId);
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const dispatch = useDispatch();

    const storedSelection = useRef<{ from: number; to: number } | null>(null);
    const restoreSelection = useActiveTiptapEditorCallback((activeEditor) => {
        if (!storedSelection.current) return;
        activeEditor?.commands.setTextSelection(storedSelection.current);
        storedSelection.current = null;
    }, []);

    const closeHyperlinkPopup = () => dispatch(closePopup(PopupIds.HYPERLINK));

    const currentUrl = useTiptapActiveEditorSelector(getCurrentUrl);
    const isSelectionEmpty = useTiptapActiveEditorSelector(getIsSelectionEmpty);

    const onPopupOpen = useActiveTiptapEditorCallback(
        (activeEditor) => {
            if (!activeEditor || !currentUrl) return;

            storedSelection.current = {
                from: activeEditor.state.selection.from,
                to: activeEditor.state.selection.to,
            };
            activeEditor.commands.extendMarkRange('link', { href: currentUrl });
        },
        [currentUrl],
    );

    useEffect(() => {
        if (isPopupOpen) {
            onPopupOpen();
        } else {
            restoreSelection();
        }
    }, [isPopupOpen, onPopupOpen]);

    const onSetHyperlink = useActiveTiptapEditorCallback(
        (activeEditor, url: string) => {
            if (!activeEditor) return;

            if (url === currentUrl) {
                // no changes to the content, but still restore focus
                activeEditor.commands.focus();
                return;
            }

            // Here we have some text specifically selected -- just update the mark for exactly that selection
            activeEditor.chain().focus().setLink({ href: url }).run();
        },
        [currentUrl],
    );

    const onClearHyperlink = useActiveTiptapEditorCallback((activeEditor) => {
        if (!activeEditor) return;
        activeEditor.chain().focus().unsetLink().run();
    }, []);

    const isMobile = isPlatformPhoneOrMobileMode(platformDetails);
    const isDisabled = isSelectionEmpty && !currentUrl;

    return (
        <div className="HyperLinkRichTextToolContainer">
            <PopupTrigger popupId={PopupIds.HYPERLINK} disabled={isDisabled} preventFocus>
                {(popupIsOpen) => {
                    const translate = useGroupTranslator();
                    const tooltip = translate('tools.editing.tooltips.hyperlink', 'Hyperlink');

                    return (
                        <TiptapHyperlinkToolButton
                            isPopupOpen={popupIsOpen}
                            disabled={isDisabled}
                            isActive={!!currentUrl}
                            tooltipText={tooltip}
                            shortcut={item.shortcut}
                        />
                    );
                }}
            </PopupTrigger>
            <ToolbarPopup
                popupId={PopupIds.HYPERLINK}
                className="hyperlink-popup"
                buttonSelector=".RichTextTool.link .icon"
                closePopup={closeHyperlinkPopup}
                showArrow={!isMobile}
                position={isMobile ? TooltipPositions.TOP : TooltipPositions.RIGHT}
                distance={isMobile ? 20 : undefined}
            >
                <HyperlinkPopup
                    onSetHyperlink={onSetHyperlink}
                    onClearHyperlink={onClearHyperlink}
                    onClose={closeHyperlinkPopup}
                    currentlyEditingId={currentlyEditingId}
                    url={currentUrl}
                    isActive={!!currentUrl}
                />
            </ToolbarPopup>
        </div>
    );
};

export default TiptapHyperlinkTool;
